import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { appConfigDb } from "./firebase";

// Remote-update control plane. A single document per environment
// (`app_config/production` | `app_config/staging`) tells the kiosk which build
// it should be running and lets ops trigger a reload without a new release.
//
// This module is a framework-agnostic external store: it lazily opens the
// Firestore listener on first subscription and tears it down on the last
// unsubscribe, so it can back a `useSyncExternalStore` hook (Milestone 4).

export type Rollout = {
  stores: "all" | string[];
  zones: "all" | string[];
};

export type KioskCommand = {
  id: string;
  action: "reload" | "hardReload";
  rollout: Rollout;
};

export type KioskDirective = {
  // CI-owned: exact build the fleet should run.
  targetVersion: string | null;
  // Prod/semver only: builds older than this are force-reloaded mid-session.
  minVersion: string | null;
  // Version-rollout controls (human-owned, set from the Firebase console).
  mode: "soft" | "hard";
  scheduledAtMs: number | null;
  rollout: Rollout;
  // Version-independent one-shot kick (human-owned).
  command: KioskCommand | null;
};

export type KioskDirectiveSource = {
  subscribe(listener: () => void): () => void;
  getSnapshot(): KioskDirective | null;
};

export type InMemoryKioskDirectiveSource = KioskDirectiveSource & {
  publish(directive: KioskDirective | null): void;
};

const DEFAULT_ROLLOUT: Rollout = { stores: "all", zones: "all" };

const APP_ENV = (import.meta.env.VITE_APP_ENV as string) || "staging";

function normalizeRollout(raw: unknown): Rollout {
  if (!raw || typeof raw !== "object") return DEFAULT_ROLLOUT;
  const r = raw as Record<string, unknown>;
  const norm = (v: unknown): "all" | string[] => {
    if (v === "all") return "all";
    if (Array.isArray(v)) return v.map(String);
    // Fail closed: a malformed/scalar targeting value (e.g. "zone-1" instead
    // of ["zone-1"]) matches nothing rather than silently expanding the reload
    // to the whole fleet.
    return [];
  };
  return { stores: norm(r.stores), zones: norm(r.zones) };
}

function normalizeCommand(raw: unknown): KioskCommand | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.id !== "string") return null;
  const action = c.action === "hardReload" ? "hardReload" : "reload";
  return { id: c.id, action, rollout: normalizeRollout(c.rollout) };
}

// Firestore Timestamps expose toMillis(); tolerate plain numbers too.
function toMillis(raw: unknown): number | null {
  if (
    raw &&
    typeof (raw as { toMillis?: () => number }).toMillis === "function"
  ) {
    return (raw as { toMillis: () => number }).toMillis();
  }
  return typeof raw === "number" ? raw : null;
}

function normalize(data: DocumentData): KioskDirective {
  return {
    targetVersion:
      typeof data.targetVersion === "string" ? data.targetVersion : null,
    minVersion: typeof data.minVersion === "string" ? data.minVersion : null,
    mode: data.mode === "hard" ? "hard" : "soft",
    scheduledAtMs: toMillis(data.scheduledAt),
    rollout: normalizeRollout(data.rollout),
    command: normalizeCommand(data.command),
  };
}

let current: KioskDirective | null = null;
const listeners = new Set<() => void>();
let unsubscribe: (() => void) | null = null;

function start() {
  unsubscribe = onSnapshot(
    doc(appConfigDb, "app_config", APP_ENV),
    (snap) => {
      current = snap.exists() ? normalize(snap.data()) : null;
      listeners.forEach((l) => l());
    },
    (err) => {
      // A listener error (offline, rules) must not crash the kiosk; the update
      // mechanism simply goes dormant until the connection recovers.
      console.error("app_config listener error", err);
    },
  );
}

/**
 * Subscribe to remote-update directive changes. Opens the Firestore listener on
 * the first subscriber and closes it when the last one leaves. Returns the
 * unsubscribe function.
 */
export function subscribeDirective(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };
}

/** Latest directive snapshot, or null if none received / no doc exists. */
export function getDirectiveSnapshot(): KioskDirective | null {
  return current;
}

export const firestoreKioskDirectiveSource: KioskDirectiveSource = {
  subscribe: subscribeDirective,
  getSnapshot: getDirectiveSnapshot,
};

export function createInMemoryDirectiveSource(
  initial: KioskDirective | null = null,
): InMemoryKioskDirectiveSource {
  let snapshot = initial;
  const sourceListeners = new Set<() => void>();

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      sourceListeners.add(listener);
      return () => sourceListeners.delete(listener);
    },
    publish(directive) {
      if (directive === snapshot) return;
      snapshot = directive;
      sourceListeners.forEach((listener) => listener());
    },
  };
}
